import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  InformationCircleIcon,
  XMarkIcon,
  TagIcon,
  EyeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useDocumentVariables } from '../../hooks/useDocumentVariables';
import { DocumentVariable, DEFAULT_VARIABLE_CATEGORIES } from '../../types/documentVariables';

interface VariablePreviewPopupProps {
  templateId?: string;
  customerId?: string;
  triggerClassName?: string;
  popupTitle?: string;
  showValues?: boolean;
}

const VariablePreviewPopup: React.FC<VariablePreviewPopupProps> = ({
  templateId,
  customerId,
  triggerClassName = "h-5 w-5 text-blue-600 hover:text-blue-700 cursor-help transition-colors",
  popupTitle = "Elérhető változók",
  showValues = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const {
    variables,
    values,
    variablesByCategory,
    loading
  } = useDocumentVariables({ 
    templateId, 
    customerId, 
    autoLoad: isOpen 
  });

  // Szűrt változók
  const filteredVariables = variables.filter(variable => {
    const matchesSearch = !searchTerm || 
      variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || variable.category === selectedCategory;
    
    return matchesSearch && matchesCategory && variable.isActive;
  });

  // Kategóriák számlálása
  const categoryCounts = Object.entries(variablesByCategory).reduce((acc, [categoryId, vars]) => {
    acc[categoryId] = vars.filter(v => v.isActive).length;
    return acc;
  }, {} as Record<string, number>);

  const getVariableTypeIcon = (type: string) => {
    switch (type) {
      case 'date': return '📅';
      case 'email': return '📧';
      case 'phone': return '📞';
      case 'currency': return '💰';
      case 'number': return '🔢';
      case 'boolean': return '☑️';
      case 'select': return '📋';
      case 'textarea': return '📝';
      default: return '📄';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'global': return 'bg-blue-100 text-blue-800';
      case 'template': return 'bg-purple-100 text-purple-800';
      case 'organization': return 'bg-green-100 text-green-800';
      case 'customer': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="group relative"
        title="Elérhető változók megtekintése"
      >
        <InformationCircleIcon className={triggerClassName} />
      </button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => setIsOpen(false)}
              />

              {/* Modal Content */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <TagIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">{popupTitle}</h2>
                        <p className="text-blue-100 text-sm">
                          {loading ? 'Betöltés...' : `${filteredVariables.length} változó elérhető`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-6 h-6 text-white" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : (
                    <>
                      {/* Search and Filters */}
                      <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        {/* Search */}
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 placeholder-gray-400"
                            placeholder="Keresés változók között..."
                          />
                        </div>

                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setSelectedCategory(null)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedCategory === null
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            Összes ({variables.filter(v => v.isActive).length})
                          </button>
                          {DEFAULT_VARIABLE_CATEGORIES.map(category => {
                            const count = categoryCounts[category.id] || 0;
                            if (count === 0) return null;
                            
                            return (
                              <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  selectedCategory === category.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {category.name} ({count})
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Variables List */}
                      {filteredVariables.length === 0 ? (
                        <div className="text-center py-12">
                          <TagIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {searchTerm || selectedCategory ? 'Nincs találat' : 'Nincsenek változók'}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400">
                            {searchTerm || selectedCategory 
                              ? 'Próbálj meg más keresési feltételeket'
                              : 'Még nincsenek definiált változók ehhez a sablonhoz'
                            }
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                          {filteredVariables.map((variable) => (
                            <motion.div
                              key={variable.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">
                                    {getVariableTypeIcon(variable.type)}
                                  </span>
                                  <div>
                                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                      {variable.name}
                                    </h4>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded font-mono text-blue-600 dark:text-blue-400">
                                        {`{{${variable.key}}}`}
                                      </code>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getScopeColor(variable.scope)}`}>
                                        {variable.scope}
                                      </span>
                                      {variable.required && (
                                        <span className="text-red-500 text-xs">*</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {variable.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {variable.description}
                                </p>
                              )}

                              {showValues && values[variable.id] && (
                                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                  <div className="text-xs text-green-700 dark:text-green-300 font-medium mb-1">
                                    Jelenlegi érték:
                                  </div>
                                  <div className="text-sm text-green-800 dark:text-green-200 font-mono">
                                    {values[variable.id]}
                                  </div>
                                </div>
                              )}

                              {variable.defaultValue && (
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                  Alapértelmezett: <span className="font-mono">{variable.defaultValue}</span>
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      )}

                      {/* Usage Instructions */}
                      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start space-x-3">
                          <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                              Használat Word dokumentumokban
                            </h4>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              Másold be a <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{KULCS}}'}</code> formátumú 
                              kódokat a Word sablonodba. A dokumentum generálásakor ezek automatikusan helyettesítődnek a megfelelő értékekkel.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VariablePreviewPopup;
